import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Loader2, X, Mail, Reply, ReplyAll, Forward, Sparkles, MessageSquareReply, CalendarPlus, Paperclip, Flame } from 'lucide-react';
import { req } from '../services/api';


/**
 * Modal visor de cuerpo completo de un correo. Carga el body on-demand desde
 * /api/correo/cuentas/{cuenta_id}/mensajes/{message_id}/body.
 *
 * Iter68: cuerpo HTML en iframe sandboxed con auto-altura. Botones Reenviar
 * y Responder a todos. Cierre con Escape o clic fuera.
 *
 * Props:
 *  - mensaje
 *  - onClose
 *  - onResponder(mensaje, bodyTexto)
 *  - onResponderTodos(mensaje, bodyTexto)
 *  - onReenviar(mensaje, bodyTexto)
 *  - onExtraerTareas(mensaje), onSugerir(mensaje), onCrearVisita(mensaje)
 *  - onToggleUrgente(mensaje)  — opcional
 */
export default function EmailViewer({
  mensaje, onClose, onResponder, onResponderTodos, onReenviar,
  onExtraerTareas, onSugerir, onCrearVisita, onToggleUrgente, fullscreen = false,
}) {
  const [body, setBody] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vistaHtml, setVistaHtml] = useState(true);

  useEffect(() => {
    if (!mensaje?.cuenta_id || !mensaje?.message_id) return;
    setLoading(true);
    req(`/api/correo/cuentas/${mensaje.cuenta_id}/mensajes/${encodeURIComponent(mensaje.message_id)}/body`)
      .then((r) => { setBody(r); setVistaHtml(!!r.body_html); })
      .catch((e) => { toast.error(`Cuerpo: ${e.message}`); setBody({ body_text: mensaje.preview || '' }); })
      .finally(() => setLoading(false));
  }, [mensaje?.cuenta_id, mensaje?.message_id, mensaje?.preview]);

  // Cierre con Escape (Iter68 FIX 2)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!mensaje) return null;

  const bodyTexto = body?.body_text || body?.body_html || mensaje.preview || '';
  const containerSize = fullscreen ? 'max-w-[90vw] w-full h-[92vh]' : 'max-w-4xl w-full max-h-[90vh]';

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid={fullscreen ? 'email-modal-fullscreen' : 'email-viewer-modal'}
    >
      <div
        className={`bg-white rounded-xl ${containerSize} flex flex-col`}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="email-viewer"
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-ink-200 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-ink-500" />
              <h3 className="text-sm font-semibold text-ink-900 truncate">
                {mensaje.subject || '(sin asunto)'}
              </h3>
              {mensaje.urgente && (
                <Badge className="bg-red-100 text-red-700 border-red-300 border" data-testid="email-viewer-urgente-badge">
                  <Flame className="w-3 h-3 mr-1" /> Urgente
                </Badge>
              )}
              {mensaje.has_attachments && <Paperclip className="w-3.5 h-3.5 text-amber-600" title="Tiene adjuntos" />}
            </div>
            <div className="text-xs text-ink-600">
              <span className="font-medium">De:</span> {mensaje.from_name ? `${mensaje.from_name} <${mensaje.from_address}>` : mensaje.from_address}
            </div>
            <div className="text-[11px] text-ink-400 mt-0.5">
              {mensaje.received_at ? new Date(mensaje.received_at).toLocaleString('es-ES') : ''}
              {Array.isArray(mensaje.to_addresses) && mensaje.to_addresses.length > 0 && (
                <span> · Para: {mensaje.to_addresses.join(', ')}</span>
              )}
              {Array.isArray(mensaje.cc_addresses) && mensaje.cc_addresses.length > 0 && (
                <span> · CC: {mensaje.cc_addresses.join(', ')}</span>
              )}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="email-viewer-cerrar"><X className="w-4 h-4" /></Button>
        </div>

        {/* Toolbar acciones */}
        <div className="px-5 py-2 border-b border-ink-100 flex items-center gap-1.5 flex-wrap">
          {onResponder && (
            <Button size="sm" variant="outline" onClick={() => onResponder(mensaje, bodyTexto)} data-testid="email-viewer-responder">
              <Reply className="w-4 h-4 mr-1.5" /> Responder
            </Button>
          )}
          {onResponderTodos && (
            <Button size="sm" variant="outline" onClick={() => onResponderTodos(mensaje, bodyTexto)} data-testid="email-viewer-responder-todos">
              <ReplyAll className="w-4 h-4 mr-1.5" /> Responder a todos
            </Button>
          )}
          {onReenviar && (
            <Button size="sm" variant="outline" onClick={() => onReenviar(mensaje, bodyTexto)} data-testid="email-viewer-reenviar">
              <Forward className="w-4 h-4 mr-1.5" /> Reenviar
            </Button>
          )}
          {onExtraerTareas && (
            <Button size="sm" variant="ghost" className="text-violet-700 hover:bg-violet-50" onClick={() => onExtraerTareas(mensaje)} data-testid="email-viewer-extraer-tareas">
              <Sparkles className="w-4 h-4 mr-1.5" /> Extraer tareas
            </Button>
          )}
          {onSugerir && (
            <Button size="sm" variant="ghost" className="text-fuchsia-700 hover:bg-fuchsia-50" onClick={() => onSugerir(mensaje)} data-testid="email-viewer-sugerir">
              <MessageSquareReply className="w-4 h-4 mr-1.5" /> Sugerir respuestas
            </Button>
          )}
          {onCrearVisita && (
            <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50" onClick={() => onCrearVisita(mensaje)} data-testid="email-viewer-crear-visita">
              <CalendarPlus className="w-4 h-4 mr-1.5" /> Visita
            </Button>
          )}
          {onToggleUrgente && (
            <Button
              size="sm"
              variant="ghost"
              className={mensaje.urgente ? 'text-red-700 hover:bg-red-50' : 'text-ink-500 hover:bg-ink-50'}
              onClick={() => onToggleUrgente(mensaje)}
              data-testid="email-viewer-toggle-urgente"
            >
              <Flame className="w-4 h-4 mr-1.5" /> {mensaje.urgente ? 'Quitar urgente' : 'Marcar urgente'}
            </Button>
          )}
          {body && body.body_html && body.body_text && (
            <div className="ml-auto flex items-center gap-1 text-xs">
              <button
                onClick={() => setVistaHtml(true)}
                className={`px-2 py-1 rounded ${vistaHtml ? 'bg-brand-50 text-brand-700 font-medium' : 'text-ink-500 hover:bg-ink-50'}`}
                data-testid="vista-html"
              >HTML</button>
              <button
                onClick={() => setVistaHtml(false)}
                className={`px-2 py-1 rounded ${!vistaHtml ? 'bg-brand-50 text-brand-700 font-medium' : 'text-ink-500 hover:bg-ink-50'}`}
                data-testid="vista-text"
              >Texto</button>
            </div>
          )}
        </div>

        {/* Cuerpo */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="text-center py-10 text-ink-500" data-testid="email-viewer-loading">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando cuerpo del mensaje…
            </div>
          )}
          {!loading && body && (
            vistaHtml && body.body_html ? (
              <iframe
                title="email-body"
                srcDoc={_sanitizeHtml(body.body_html, { cuentaId: mensaje.cuenta_id, messageId: mensaje.message_id })}
                sandbox="allow-same-origin"
                style={{ width: '100%', minHeight: '400px', border: 'none', display: 'block' }}
                onLoad={(e) => {
                  try {
                    const h = e.target.contentDocument?.body?.scrollHeight;
                    if (h) e.target.style.height = `${Math.min(h + 24, 4000)}px`;
                  } catch (_) { /* cross-origin noop */ }
                }}
                data-testid="email-viewer-body-html"
              />
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-sm text-ink-800" data-testid="email-viewer-body-text">
                {body.body_text || body.body_html || '(sin contenido)'}
              </pre>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// Saneamiento básico de HTML. ctx: { cuentaId, messageId } para resolver cid:
function _sanitizeHtml(html, ctx = {}) {
  if (!html) return '';
  let s = String(html);
  s = s.replace(/<script[\s\S]*?<\/script>/gi, '');
  s = s.replace(/<style[\s\S]*?<\/style>/gi, '');
  s = s.replace(/\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  s = s.replace(/(href|src)\s*=\s*("|')javascript:[^"']*\2/gi, '$1="#"');
  s = s.replace(/(src|href|background)\s*=\s*("|')http:\/\//gi, '$1=$2https://');
  if (ctx.cuentaId && ctx.messageId) {
    const token = localStorage.getItem('token') || '';
    const base = process.env.REACT_APP_BACKEND_URL;
    s = s.replace(
      /src\s*=\s*(["'])cid:([^"']+)\1/gi,
      (_, q, cid) => `src="${base}/api/correo/cuentas/${ctx.cuentaId}/mensajes/${encodeURIComponent(ctx.messageId)}/inline/${encodeURIComponent(cid)}?t=${token}"`,
    );
  }
  return s;
}
